// List of supported special chars in password
// eslint-disable-next-line max-len
export const PASSWORD_SPECIAL_CHARS = [
  '@',
  '#',
  '$',
  '%',
  '^',
  '&',
  '*',
  '-',
  '_',
  '!',
  '+',
  '=',
  '[',
  ']',
  '{',
  '}',
  '|',
  '\\',
  ':',
  "'",
  ',',
  '.',
  '?',
  '/',
  '`',
  '~',
  '"',
  '(',
  ')',
  ';',
];

// Regex that validates if the password contains at least one of the supported special chars
export const PASSWORD_SPECIAL_CHARS_REGEX = new RegExp(`[\\${PASSWORD_SPECIAL_CHARS.join('\\')}]`);

// Number of different checks required for the password (ie: any X of lowercase, uppercase, number, special char)
export const PASSWORD_MIN_COMPLEXITY_CHECKS = 3;

// Minimal number of characters
export const PASSWORD_MIN_CHARS = 8;

/**
 * Validate if a password has enough characters.
 * @param password Password to validate
 * @returns True if the password has enough characters.
 */
export const passwordHasMinLength = (password: string): boolean => password.length >= PASSWORD_MIN_CHARS;

/**
 * Validate if a password contains a number.
 * @param password Password to validate
 * @returns True if the password contains a number; false otherwise.
 */
export const passwordHasNumber = (password: string): boolean => /[0-9]/.test(password);

/**
 * Validate if a password contains a lowercase character.
 * @param password Password to validate
 * @returns True if the password contains a lowercase character; false otherwise.
 */
export const passwordHasLowerCase = (password: string): boolean => /[a-z]/.test(password);

/**
 * Validate if a password contains an uppercase character.
 * @param password Password to validate
 * @returns True if the password contains an uppercase character; false otherwise.
 */
export const passwordHasUpperCase = (password: string): boolean => /[A-Z]/.test(password);

/**
 * Validate if a password contains a special char.
 * @param password Password to validate
 * @returns True if the password contains a special char; false otherwise.
 */
export const passwordHasSpecialChar = (password: string): boolean => PASSWORD_SPECIAL_CHARS_REGEX.test(password);

/**
 * Validate if a password meets the minimal complexity requirements.
 * @param password Password to validate
 * @returns True if the password meets complexity requirements; false otherwise.
 */
export const passwordMeetsComplexityReq = (password: string): boolean => {
  const checks = [passwordHasLowerCase, passwordHasUpperCase, passwordHasNumber, passwordHasSpecialChar];

  return checks.filter(check => check(password)).length >= PASSWORD_MIN_COMPLEXITY_CHECKS;
};

/**
 * Validate if a password meets all requirements.
 * @param password Password to validate
 * @returns True if the password is valid; false otherwise.
 */
export const passwordIsValid = (password: string) => passwordMeetsComplexityReq(password) && passwordHasMinLength(password);

/**
 * Opens a popup for SSO authentication and updates the window URL upon completion.
 * @param ssoUrl The URL for the SSO authentication.
 * @param callbackUrl The URL to navigate to after SSO is complete.
 */
export const handleSSOPopup = (ssoUrl: string, callbackUrl: string) => {
  const popup = window.open(ssoUrl, 'SSO', 'width=600,height=700,scrollbars=yes,resizable=yes');

  if (!popup) {
    console.error('Failed to open SSO popup.');
    return;
  }

  const interval = setInterval(() => {
    if (popup.closed) {
      clearInterval(interval);
    }
  }, 500);

  window.addEventListener('message', event => {
    if (event.data === 'SSO_COMPLETE') {
      popup.close();
      clearInterval(interval);

      // Update the browser's window URL
      if (callbackUrl) {
        window.location.href = callbackUrl;
      }
    }
  });
};
